import {
  autoSignIn,
  confirmSignIn,
  confirmSignUp,
  resendSignUpCode,
  signIn,
  signUp,
} from "@aws-amplify/auth";

/** Whether the user was routed through the sign-up or sign-in path. */
export type OtpMode = "signup" | "signin";

/**
 * Initiate email OTP for sign-in or sign-up.
 *
 * Mirrors the mobile flow (`mobile/src/auth/credentials.ts`): try signUp
 * first because Cognito's USER_AUTH flow silently accepts sign-in for
 * non-existent users (user enumeration protection). If the email is
 * already registered, fall through to a sign-in challenge.
 *
 * Returns the OtpMode so the verify step knows which confirm path to call.
 */
export async function requestEmailOtp(email: string): Promise<OtpMode> {
  try {
    await signUp({
      username: email,
      password: _randomPassword(),
      options: { userAttributes: { email }, autoSignIn: true },
    });
    return "signup";
  } catch (error) {
    if (!String(error).includes("UsernameExistsException")) {
      throw error;
    }
  }

  const { nextStep } = await signIn({
    username: email,
    options: { authFlowType: "USER_AUTH", preferredChallenge: "EMAIL_OTP" },
  });
  if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE") {
    return "signin";
  }
  throw new Error(`Unexpected auth step: ${nextStep.signInStep}`);
}

/** Confirm OTP for a returning user (sign-in challenge path). */
export async function confirmSignInOtp(code: string): Promise<void> {
  const { isSignedIn } = await confirmSignIn({ challengeResponse: code });
  if (!isSignedIn) {
    throw new Error("Sign-in incomplete after OTP confirmation");
  }
}

/** Confirm OTP for a new user (sign-up verification path), then auto sign-in. */
export async function confirmSignUpOtp(email: string, code: string): Promise<void> {
  await confirmSignUp({ username: email, confirmationCode: code });
  await autoSignIn();
}

/** Resend OTP. signup → resend the verification code. signin → restart the challenge. */
export async function resendEmailOtp(email: string, mode: OtpMode): Promise<void> {
  if (mode === "signup") {
    await resendSignUpCode({ username: email });
  } else {
    await signIn({
      username: email,
      options: { authFlowType: "USER_AUTH", preferredChallenge: "EMAIL_OTP" },
    });
  }
}

/** Generate a crypto-random password that satisfies standard Cognito policies.
 *
 * Used only during signUp() — the user never sees or types it. EMAIL_OTP
 * is the actual auth factor; the password exists solely because Cognito's
 * signUp API requires one.
 */
function _randomPassword(): string {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  const base = Array.from(bytes, (b) => chars[b % chars.length]).join("");
  return "Aa1!" + base.slice(4);
}
